import type { Building, Floor, Room, Tour } from '@/types/tour';
import {
  currentBuildingId,
  currentFloorId,
  currentRoomId,
} from '@/core/state';
import { getBuilding, getFloor, findRoomLocation } from '@/core/tour-utils';
import { Floorplan } from '@/components/floorplan';

// Sidebar-Aufbau:
//
//   ┌──────────────────────────────────────┐
//   │ [Haus 1] [Haus 2]                    │  Building-Tabs
//   ├──────────────────────────────────────┤
//   │ [EG] [1.OG] [2.OG]                   │  Floor-Tabs (steuert Floorplan)
//   ├──────────────────────────────────────┤
//   │ ┌──────────────────────────────────┐ │
//   │ │   Raster-Floorplan + Hotspots    │ │
//   │ └──────────────────────────────────┘ │
//   ├──────────────────────────────────────┤
//   │ Räume                                │
//   │ ▾ Büro (4)                           │  klappbare Kategorie-Sektionen
//   │   • 1.1.15 (1. OG)                   │
//   │   • 1.3.1 (3. OG)                    │
//   │ ▾ Labor (2)                          │
//   │   …                                  │
//   │ ▸ Treppenhaus / Flure (3)            │  zugeklappte Sektion
//   └──────────────────────────────────────┘
//
// Wenn die Tour Kategorien definiert hat, werden Building-/Floor-Tabs und
// Floorplan ausgeblendet (data-sidebar-mode='categories'); die Raumliste
// gruppiert alle Raeume des aktiven Gebaeudes nach Kategorie. Sektionen
// sind klappbar, leere Kategorien werden ausgelassen. Ohne Kategorien
// faellt die Liste auf das Floor-basierte Verhalten zurueck.

export class Sidebar {
  private root: HTMLElement;
  private buildingTabs!: HTMLDivElement;
  private floorTabs!: HTMLDivElement;
  private floorplanHost!: HTMLDivElement;
  private roomList!: HTMLUListElement;
  private floorplan: Floorplan;
  private collapsedCategoryIds = new Set<string>();

  constructor(
    parent: HTMLElement,
    private tour: Tour,
  ) {
    this.root = parent;
    this.root.classList.add('app-sidebar');
    this.buildSkeleton();
    this.floorplan = new Floorplan(this.floorplanHost);

    // Im Kategorien-Modus blenden wir Building-/Floor-Tabs und den
    // Floorplan via data-Attribut aus (CSS in _sidebar.scss); die Navigation
    // laeuft komplett ueber Kategorien-Chips + Raumliste.
    this.root.dataset.sidebarMode = this.hasCategories() ? 'categories' : 'classic';

    this.renderBuildingTabs();

    currentBuildingId.subscribe((id) => this.onBuildingChange(id));
    currentFloorId.subscribe((id) => this.onFloorChange(id));
    currentRoomId.subscribe((id) => this.markActiveRoom(id));
  }

  private hasCategories(): boolean {
    return (this.tour.categories?.length ?? 0) > 0;
  }

  private buildSkeleton(): void {
    this.root.innerHTML = `
      <div class="sidebar-tabs sidebar-tabs--building" role="tablist" aria-label="Gebaeude"></div>
      <div class="sidebar-tabs sidebar-tabs--floor" role="tablist" aria-label="Etage"></div>
      <div class="sidebar-floorplan"></div>
      <div class="sidebar-rooms">
        <h2 class="sidebar-rooms__title">Räume</h2>
        <ul class="sidebar-rooms__list"></ul>
      </div>
      <button type="button" class="app-sidebar__toggle" data-role="toggle"
              aria-label="Navigation ein-/ausklappen">
        <span class="app-sidebar__toggle-icon" aria-hidden="true">⟨</span>
        <span class="app-sidebar__toggle-text">Einklappen</span>
      </button>
    `;
    this.root.querySelector<HTMLButtonElement>('[data-role="toggle"]')?.addEventListener('click', () => {
      const collapsed = document.body.dataset.sidebarCollapsed === 'true';
      if (collapsed) {
        delete document.body.dataset.sidebarCollapsed;
      } else {
        document.body.dataset.sidebarCollapsed = 'true';
      }
      this.updateToggleArrow();
    });
    this.updateToggleArrow();
    const q = <T extends Element>(sel: string): T => {
      const el = this.root.querySelector<T>(sel);
      if (!el) throw new Error(`Sidebar: ${sel} fehlt`);
      return el;
    };
    this.buildingTabs = q<HTMLDivElement>('.sidebar-tabs--building');
    this.floorTabs = q<HTMLDivElement>('.sidebar-tabs--floor');
    this.floorplanHost = q<HTMLDivElement>('.sidebar-floorplan');
    this.roomList = q<HTMLUListElement>('.sidebar-rooms__list');
  }

  private renderBuildingTabs(): void {
    this.buildingTabs.innerHTML = '';
    this.tour.buildings.forEach((b) => {
      const btn = this.createTab(b.shortName, b.name, () =>
        this.selectBuilding(b),
      );
      btn.dataset.buildingId = b.id;
      this.buildingTabs.appendChild(btn);
    });
    this.updateBuildingTabsActive(currentBuildingId.get());
  }

  private renderFloorTabs(building: Building): void {
    this.floorTabs.innerHTML = '';
    building.floors.forEach((f) => {
      const btn = this.createTab(f.shortName, f.name, () =>
        this.selectFloor(building, f),
      );
      btn.dataset.floorId = f.id;
      this.floorTabs.appendChild(btn);
    });
    this.updateFloorTabsActive(currentFloorId.get());
  }

  // Re-rendert die Raumliste passend zum aktuellen Modus (cross-floor mit
  // Kategorien oder klassisch nach Floor). Wird bei Building-/Floor-Wechsel
  // und bei Chip-Toggle aufgerufen.
  private renderActiveRoomList(): void {
    if (this.hasCategories()) {
      const b = getBuilding(this.tour, currentBuildingId.get());
      if (b) this.renderRoomListCrossFloor(b);
    } else {
      const f = getFloor(this.tour, currentBuildingId.get(), currentFloorId.get());
      if (f) this.renderRoomListForFloor(f);
    }
  }

  // Klassischer Modus (ohne Kategorien): Raeume des aktuellen Floors.
  private renderRoomListForFloor(floor: Floor): void {
    this.roomList.innerHTML = '';
    const defaultId = this.tour.defaultRoomId;
    floor.rooms.forEach((r) => {
      const li = document.createElement('li');
      li.className = 'sidebar-rooms__item';
      li.appendChild(this.createRoomButton(r, { meta: r.meta }, defaultId));
      this.roomList.appendChild(li);
    });
    this.markActiveRoom(currentRoomId.get());
  }

  // Kategorien-Modus: alle Raeume des Buildings, gruppiert nach Kategorie in
  // klappbaren Sektionen. Leere Kategorien werden ausgelassen — wenn ein
  // Gebaeude z. B. kein Technikum hat, taucht die Sektion gar nicht erst auf.
  // Raeume ohne categoryId landen in einer Fallback-Sektion am Ende, damit
  // sie nicht verloren gehen.
  private renderRoomListCrossFloor(building: Building): void {
    this.roomList.innerHTML = '';
    const defaultId = this.tour.defaultRoomId;
    const cats = this.tour.categories ?? [];

    // Raeume des Buildings einmal pro Kategorie sammeln (in Floor-Reihenfolge
    // = stabile Reihenfolge innerhalb einer Sektion).
    interface RoomEntry { room: Room; floorLabel: string; }
    const byCategory = new Map<string, RoomEntry[]>();
    const uncategorized: RoomEntry[] = [];
    building.floors.forEach((f) => {
      f.rooms.forEach((r) => {
        const entry: RoomEntry = { room: r, floorLabel: f.shortName };
        if (r.categoryId && cats.some((c) => c.id === r.categoryId)) {
          let list = byCategory.get(r.categoryId);
          if (!list) {
            list = [];
            byCategory.set(r.categoryId, list);
          }
          list.push(entry);
        } else {
          uncategorized.push(entry);
        }
      });
    });

    let totalSections = 0;

    cats.forEach((c) => {
      const entries = byCategory.get(c.id);
      if (!entries || entries.length === 0) return; // leere Kategorie -> skip
      this.roomList.appendChild(this.renderCategorySection(c.id, c.label, c.color, entries, defaultId));
      totalSections += 1;
    });

    if (uncategorized.length > 0) {
      this.roomList.appendChild(
        this.renderCategorySection('__uncategorized', 'Ohne Kategorie', undefined, uncategorized, defaultId),
      );
      totalSections += 1;
    }

    if (totalSections === 0) {
      const li = document.createElement('li');
      li.className = 'sidebar-rooms__empty';
      li.textContent = 'Keine Raeume in diesem Gebaeude.';
      this.roomList.appendChild(li);
    }
    this.markActiveRoom(currentRoomId.get());
  }

  private renderCategorySection(
    sectionId: string,
    label: string,
    color: string | undefined,
    entries: { room: Room; floorLabel: string }[],
    defaultId: string | undefined,
  ): HTMLLIElement {
    const collapsed = this.collapsedCategoryIds.has(sectionId);
    const li = document.createElement('li');
    li.className = `sidebar-cat-section${collapsed ? ' is-collapsed' : ''}`;
    li.dataset.catId = sectionId;
    if (color) li.style.setProperty('--cat-color', color);

    const head = document.createElement('button');
    head.type = 'button';
    head.className = 'sidebar-cat-section__head';
    head.setAttribute('aria-expanded', String(!collapsed));
    head.innerHTML = `
      <span class="sidebar-cat-section__caret" aria-hidden="true">▾</span>
      <span class="sidebar-cat-section__label">${escapeHtml(label)}</span>
      <span class="sidebar-cat-section__count">${entries.length}</span>
    `;
    head.addEventListener('click', () => {
      if (this.collapsedCategoryIds.has(sectionId)) this.collapsedCategoryIds.delete(sectionId);
      else this.collapsedCategoryIds.add(sectionId);
      const nowCollapsed = this.collapsedCategoryIds.has(sectionId);
      li.classList.toggle('is-collapsed', nowCollapsed);
      head.setAttribute('aria-expanded', String(!nowCollapsed));
    });

    const rooms = document.createElement('ul');
    rooms.className = 'sidebar-cat-section__rooms';
    entries.forEach((e) => {
      const item = document.createElement('li');
      item.className = 'sidebar-rooms__item';
      item.appendChild(this.createRoomButton(e.room, { floor: e.floorLabel, meta: e.room.meta }, defaultId));
      rooms.appendChild(item);
    });

    li.appendChild(head);
    li.appendChild(rooms);
    return li;
  }

  private createRoomButton(
    r: Room,
    labels: { floor?: string; meta?: string },
    defaultId: string | undefined,
  ): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'sidebar-rooms__btn';
    btn.dataset.roomId = r.id;
    const isDefault = r.id === defaultId;
    const floorSuffix = labels.floor
      ? ` <span class="sidebar-rooms__floor">(${escapeHtml(labels.floor)})</span>`
      : '';
    const meta = (labels.meta ?? '').trim();
    const metaLine = meta ? `<span class="sidebar-rooms__meta">${escapeHtml(meta)}</span>` : '';
    btn.innerHTML = `
      <span class="sidebar-rooms__name">
        ${isDefault ? '<span class="sidebar-rooms__star" title="Start-Raum">★</span> ' : ''}${escapeHtml(r.name)}${floorSuffix}
      </span>
      ${metaLine}
    `;
    btn.addEventListener('click', () => this.navigateToRoom(r.id));
    return btn;
  }

  // Wechselt zum Raum — und zieht Building/Floor automatisch mit, falls
  // der Raum auf einer anderen Etage liegt (cross-floor-Navigation aus
  // dem Kategorien-Filter).
  private navigateToRoom(roomId: string): void {
    const loc = findRoomLocation(this.tour, roomId);
    if (!loc) return;
    if (currentBuildingId.get() !== loc.building.id) currentBuildingId.set(loc.building.id);
    if (currentFloorId.get() !== loc.floor.id) currentFloorId.set(loc.floor.id);
    currentRoomId.set(roomId);
  }

  private createTab(
    label: string,
    fullName: string,
    onClick: () => void,
  ): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'sidebar-tab';
    btn.setAttribute('role', 'tab');
    btn.title = fullName;
    btn.textContent = label;
    btn.addEventListener('click', onClick);
    return btn;
  }

  // ============ Selection-Handler ============

  private selectBuilding(b: Building): void {
    if (currentBuildingId.get() === b.id) return;
    currentBuildingId.set(b.id);
    // Beim Building-Wechsel automatisch erste Etage + ersten Raum waehlen.
    const f = b.floors[0];
    if (!f) return;
    currentFloorId.set(f.id);
    const r = f.rooms[0];
    if (r) currentRoomId.set(r.id);
  }

  private selectFloor(b: Building, f: Floor): void {
    if (currentFloorId.get() === f.id && currentBuildingId.get() === b.id) return;
    currentFloorId.set(f.id);
    const r = f.rooms[0];
    if (r) currentRoomId.set(r.id);
  }

  // ============ Reaktionen auf State ============

  private onBuildingChange(buildingId: string): void {
    this.updateBuildingTabsActive(buildingId);
    const b = getBuilding(this.tour, buildingId);
    if (b) this.renderFloorTabs(b);
  }

  private onFloorChange(floorId: string): void {
    this.updateFloorTabsActive(floorId);
    const f = getFloor(this.tour, currentBuildingId.get(), floorId);
    if (!f) return;
    // Im Kategorien-Modus wird der Floorplan nicht angezeigt — Rendern
    // sparen wir uns (das laedt sonst unnoetig das Floorplan-Bild).
    if (!this.hasCategories()) this.floorplan.render(f);
    this.renderActiveRoomList();
  }

  private markActiveRoom(roomId: string): void {
    this.roomList.querySelectorAll<HTMLButtonElement>('.sidebar-rooms__btn').forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.roomId === roomId);
    });
  }

  private updateToggleArrow(): void {
    const iconEl = this.root.querySelector<HTMLElement>('[data-role="toggle"] .app-sidebar__toggle-icon');
    const textEl = this.root.querySelector<HTMLElement>('[data-role="toggle"] .app-sidebar__toggle-text');
    if (!iconEl || !textEl) return;
    const collapsed = document.body.dataset.sidebarCollapsed === 'true';
    const right = document.body.dataset.layoutSidebar === 'right';
    // Pfeil zeigt in Richtung "wo der Inhalt ist": collapsed -> nach
    // innen (Inhalt aufklappen), expanded -> nach aussen (zuklappen).
    if (right) iconEl.textContent = collapsed ? '⟨' : '⟩';
    else iconEl.textContent = collapsed ? '⟩' : '⟨';
    textEl.textContent = collapsed ? 'Ausklappen' : 'Einklappen';
  }

  private updateBuildingTabsActive(buildingId: string): void {
    this.buildingTabs.querySelectorAll<HTMLButtonElement>('.sidebar-tab').forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.buildingId === buildingId);
    });
  }

  private updateFloorTabsActive(floorId: string): void {
    this.floorTabs.querySelectorAll<HTMLButtonElement>('.sidebar-tab').forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.floorId === floorId);
    });
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

